import ComponentManager from '@/Management/ComponentManager';
import ToolBarLinkViewModel from '@/Components/Links/ToolBarLinkViewModel';
import GlobalConfiguration from '@/Management/GlobalConfiguration';
import * as CoreFrameDTOs from "coreframetypings/dto";
import Loader from '@/Utility/Loader';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import ObjectDisconnectionHelperType from '@/Utility/Association/ObjectDisconnectionHelper';
import Utilities from '@/Utility/Utilities';
import { IRefreshable, ISelectable } from '#/DefaultViewExtensionInterfaces';
import AssociationHelper from '@/Utility/Association/AssociationHelper';

export default class DisconnectLinkViewModel extends ToolBarLinkViewModel {

    protected listView: KnockoutObservable<CoreFrameDTOs.IListViewDTO>;
    protected contextParameters: KnockoutObservable<any>;
    protected contextPdoId: KnockoutObservable<any>;
    protected contextTypeName: KnockoutObservable<string>;
    protected contextRelationName: KnockoutObservable<string>;
    protected typeName: KnockoutObservable<string>;
    protected detailViewItem: CoreFrameDTOs.IDVGItemDTO;
    protected dataSource: ISelectable & IRefreshable;

    public constructor(protected params: any) {
        super($.extend(params,
            {
                tooltipErgName: 'DISCONNECT_TOOLTIP',
                imageSource: GlobalConfiguration.inlineImages.TOOLBAR_DISCONNECT,
                locator: 'disconnect'
            }));
        this.contextPdoId = this.observableOrDefault(params.contextPdoId, this.context.genericObject.pdoId);
        this.contextTypeName = this.observableOrDefault(params.contextTypeName, this.context.genericObject.pdTypeName);

        if (params.pdTypeName)
            this.typeName = this.observableOrDefault(params.pdTypeName);
        else
            this.typeName = this.defineComputed(() => this.listView().typeName);

        if (params.contextRelationName)
            this.contextRelationName = this.observableOrDefault(params.contextRelationName);
        else
            this.contextRelationName = this.defineComputed(() => this.listView().relationName);

        this.setRequiredObservables();
    }

    public createIsEnabledObservable(): KnockoutObservable<boolean> {
        this.setRequiredObservables();

        const parameterEnabled = this.observableOrDefault(this.params.isEnabled, true);
        return this.defineComputed(() => {
            if (!parameterEnabled())
                return false;

            return !Utilities.isNullOrUndefined(this.dataSource) && this.dataSource.selected().length > 0;
        });
    }

    /**
     * Da bereits im Konstruktor der Oberklassen Methoden aufgerufen werden, die auf Member dieser Klasse zugreifen
     * muss dies in eine Methode ausgelagert werden
     */
    protected setRequiredObservables(): void {
        if (Utilities.isNullOrUndefined(this.dataSource)) {
            this.dataSource = this.paramOrDefault(this.params.dataSource, null);
        }

        if (Utilities.isNullOrUndefined(this.listView)) {
            this.listView = this.observableOrDefault(this.params.listView);
        }

        if (Utilities.isNullOrUndefined(this.detailViewItem)) {
            this.detailViewItem = this.paramOrDefault(this.params.detailViewItem, null);
        }
    }

    /**
     * @override 
     * @param openInNewWindow
     * @param event
     */
    public onClick(openInNewWindow?: boolean, event?: JQuery.Event): void {
        if (!this.dataSource)
            return;
        const selected = this.getPdoIds(this.dataSource.selected());
        this.disconnectInternal(selected);
    }

    /**
     * Returns the selected pdo-ids
     * @param rows
     */
    protected getPdoIds(rows: any[]): string[] {
        return rows;
    }

    private disconnectInternal(toPdoIds: Array<string>): void {
        const pdoID = this.contextPdoId();
        const associationName = this.contextRelationName();
        const pdTypeName = this.typeName();
        const nestedContextName = this.context.getNestedContextPath();

        this.tryToDisconnect(pdoID, associationName, pdTypeName, toPdoIds, nestedContextName)
            .then(disconnectAllowed => {
                if (disconnectAllowed) {
                    this.disconnect(pdoID, associationName, pdTypeName, toPdoIds, nestedContextName);
                }
            });
    }

    /**
     * Wird vorm Löschen aufgerufen und versucht zunächst zu trennen
     * @param pdoId
     * @param associationName
     * @param topdTypeName
     * @param toPdoIds
     * @param nestedContextName
     */
    protected tryToDisconnect(pdoId: string,
        associationName: string,
        topdTypeName: string,
        toPdoIds: Array<string>,
        nestedContextName: string): JQueryPromise<boolean> {
        const ObjectDisconnectionHelper = ComponentManager.getUtility<typeof ObjectDisconnectionHelperType>('ObjectDisconnectionHelper');
        const odh = new ObjectDisconnectionHelper({
            contextPdoId: pdoId,
            associationName: associationName,
            pdTypeName: topdTypeName,
            affectedObjectIds: toPdoIds,
            disconnectionCallback: () => {
                this.disconnect(pdoId, associationName, topdTypeName, toPdoIds, nestedContextName);
            }
        });

        odh.ExecuteCheck();

        return Utilities.resolveImmediately(false);
    }

    /**
     * Die Ausführung des eigentlich Trennens
     * @param pdoId
     * @param associationName
     * @param topdTypeName
     * @param toPdoIds
     * @param nestedContextName
     */
    protected disconnect(pdoId: string, associationName: string, topdTypeName: string, toPdoIds: Array<string>, nestedContextName: string): void {
        $.when(this.context.notifyBeforeUpdatedAssociation(associationName, 'disconnect', { toPdoIds: toPdoIds }))
            .then((doUpdateAssociationSync) => {
                if (doUpdateAssociationSync == null || doUpdateAssociationSync === true) {
                    Loader.getInstance().show();
                    this.callDisconnectOperation(pdoId, associationName, topdTypeName, toPdoIds, nestedContextName)
                        .then((changeObject: any) => {
                            this.disconnectCallback(changeObject);
                        }).always(() => {
                            Loader.getInstance().hide();
                        });
                }
            });
    }

    /**
     * Ruft die Methode zum Trennen auf
     * @param pdoId
     * @param associationName
     * @param topdTypeName
     * @param toPdoIds
     * @param nestedContextName
     */
    protected callDisconnectOperation(pdoId: string, associationName: string, topdTypeName: string, toPdoIds: Array<string>, nestedContextName: string): JQueryPromise<any> {
        return RADEnvironmentProxy.getInstance().disconnectMany({
            pdoID: pdoId,
            assoziationName: associationName,
            topdTypeName: topdTypeName,
            toPDOIDs: toPdoIds,
            nestedContextName: nestedContextName
        }).promise();
    }

    /**
     * Das Callback, welches nach dem Disconnect ausgeführt wird
     * @param callbackResult
     */
    protected disconnectCallback(callbackResult: any): void {
        // Auch im Fehlerfall kann sich die Assoziation geändert haben, da alle Objekte bis zum Fehler getrennt werden
        // Nachfolgende Elemente (ab Index, wo der Fehler auftritt) werden nicht mehr getrennt
        this.validateStatus(callbackResult as CoreFrameDTOs.IChangeObjectDTO);

        // Alle geänderten Assoziationen werden geladen, auch wenn sie Lazy sind
        AssociationHelper.activateDataLoading(this.context, callbackResult.touchedMembers, this.listView().relationName);
        this.dataSource?.refresh();

        this.context.genericObject.isModified(true);
        this.context.notifyUpdatedAssociation(this.listView().relationName);
        this.context.notifyFactoryRefreshed(this.detailViewItem);
    }
}

ComponentManager.setViewModel('DisconnectLink', DisconnectLinkViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');